// components/DashboardComponents/FollowUpDashboard.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, Clock, Phone, AlertCircle, ChevronRight, Search, Eye } from 'lucide-react';
import api from '../../common/apiClient';
import SummaryApi from '../../common/SummaryApi';
import FollowUpForm from '../ClientVisitsComponents/FollowUpForm';
import { toast } from 'react-toastify';
import { FollowUpDashboardSkeleton } from './SkeletonLoader';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import SelectDropdown from '../../customStyles/SelectDropdown';

const FollowUpDashboard = ({ viewType = 'all', userId = null }) => {
  const navigate = useNavigate();
  // const location = useLocation();
  // const [isInitialized, setIsInitialized] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [todayFollowUps, setTodayFollowUps] = useState([]); // Add this state
  const [upcomingFollowUps, setUpcomingFollowUps] = useState([]);
  const [overdueFollowUps, setOverdueFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('today');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDivision, setSelectedDivision] = useState('all');
  const [divisions, setDivisions] = useState([]);
  const user = useSelector((state) => state?.user?.user);
  const portalName = user?.organization?.settings?.portalName;
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  // Follow-up form state
  const [showFollowUpForm, setShowFollowUpForm] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState(null);

  // useEffect(() => {
  //   if (isInitialized) return;

  //   const params = new URLSearchParams(location.search);

  //   setSelectedDivision(params.get("divisionId") || "all");
  //   setSelectedUser(params.get("userId") || "");
  //   setSearchTerm(params.get("search") || "");

  //   setIsInitialized(true);
  // }, [location.search, isInitialized]);

  // useEffect(() => {
  // if (!isInitialized) return;

  // const params = new URLSearchParams();

  // if (selectedDivision !== "all") {
  //   params.set("divisionId", selectedDivision);
  // }

  // if (selectedUser) {
  //   params.set("userId", selectedUser);
  // }

  // if (searchTerm) {
  //   params.set("search", searchTerm);
  // }

  // navigate(`?${params.toString()}`, { replace: true });
  // }, [selectedDivision, selectedUser, searchTerm, isInitialized, navigate]);

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (viewType === 'user' && userId) {
        params.append('onlyMyData', 'true');
      }
      if (selectedUser) {
        params.append('userId', selectedUser);
      }
      if (selectedDivision !== 'all') {
        params.append('divisionId', selectedDivision);
      }
      const response = await api.get(`${SummaryApi.getFollowUpDashboard.url}?${params.toString()}`);
      if (response.data.success) {
        setDashboardData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Failed to fetch follow-up dashboard');
    } finally {
      setLoading(false);
    }
  }, [viewType, userId, selectedUser, selectedDivision]);

  // Fetch today's follow-ups
  const fetchTodayFollowUps = useCallback(async () => {
    try {
      const params = new URLSearchParams();

      if (viewType === 'user' && userId) {
        params.append('onlyMyData', 'true');
      }
      if (selectedUser) {
        params.append('userId', selectedUser);
      }
      if (selectedDivision !== 'all') {
        params.append('divisionId', selectedDivision);
      }

      const response = await api.get(`${SummaryApi.getTodayFollowUps.url}?${params.toString()}`);
      if (response.data.success) {
        setTodayFollowUps(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching today follow-ups:', error);
    }
  }, [viewType, userId, selectedUser, selectedDivision]);

  // Fetch upcoming follow-ups
  const fetchUpcomingFollowUps = useCallback(async (days = 7) => {
    try {
      const params = new URLSearchParams({
        days: days.toString(),
        // limit: '2000'
      });

      if (viewType === 'user' && userId) {
        params.append('onlyMyData', 'true');
      }
      if (selectedUser) {
        params.append('userId', selectedUser);
      }
      if (selectedDivision !== 'all') {
        params.append('divisionId', selectedDivision);
      }

      const response = await api.get(`${SummaryApi.getUpcomingFollowUps.url}?${params.toString()}`);
      if (response.data.success) {
        setUpcomingFollowUps(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching upcoming follow-ups:', error);
    }
  }, [viewType, userId, selectedUser, selectedDivision]);

  // Fetch overdue follow-ups
  const fetchOverdueFollowUps = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        // limit: '2000'
      });

      if (viewType === 'user' && userId) {
        params.append('onlyMyData', 'true');
      }
      if (selectedUser) {
        params.append('userId', selectedUser);
      }
      if (selectedDivision !== 'all') {
        params.append('divisionId', selectedDivision);
      }

      const response = await api.get(`${SummaryApi.getOverdueFollowUps.url}?${params.toString()}`);
      if (response.data.success) {
        setOverdueFollowUps(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching overdue follow-ups:', error);
    }
  }, [viewType, userId, selectedUser, selectedDivision]);

  // Fetch divisions
  const fetchDivisions = useCallback(async () => {
    try {
      const response = await api.get(SummaryApi.getAllDivisions.url);
      if (response.data.success) {
        setDivisions(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching divisions:', error);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
    fetchDivisions();
  }, [fetchDashboardData, fetchDivisions]);

  useEffect(() => {
    if (activeTab === 'today') {
      fetchTodayFollowUps();
    } else if (activeTab === 'upcoming') {
      fetchUpcomingFollowUps();
    } else if (activeTab === 'overdue') {
      fetchOverdueFollowUps();
    }
  }, [activeTab, fetchTodayFollowUps, fetchUpcomingFollowUps, fetchOverdueFollowUps]);

  // Filter follow-ups based on search
  const filterFollowUps = (followUps) => {
    if (!searchTerm) return followUps;

    return followUps.filter(followUp =>
      followUp.clientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      followUp.contactName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      followUp.clientId?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Format date
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Get days until follow-up
  const getDaysUntil = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const followUpDate = new Date(date);
    followUpDate.setHours(0, 0, 0, 0);
    const diffTime = followUpDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Handle follow-up click
  const handleFollowUpClick = (visit) => {
    setSelectedVisit(visit);
    setShowFollowUpForm(true);
  };
  const getCurrentList = () => {
    if (activeTab === "today") return todayFollowUps;
    if (activeTab === "upcoming") return upcomingFollowUps;
    if (activeTab === "overdue") return overdueFollowUps;
    return [];
  };
    // Handle view details click
const handleViewDetails = (visit) => {
  const currentList = filterFollowUps(getCurrentList());

  navigate(`/${portalName}/sales/leads/view/${visit._id}`, {
    state: {
      allIds: currentList.map(item => item._id)
    }
  });
};

  // Handle follow-up success
  const handleFollowUpSuccess = () => {
    setShowFollowUpForm(false);
    fetchDashboardData();

    // Refresh the appropriate list
    if (activeTab === 'today') {
      fetchTodayFollowUps();
    } else if (activeTab === 'upcoming') {
      fetchUpcomingFollowUps();
    } else if (activeTab === 'overdue') {
      fetchOverdueFollowUps();
    }
  };

  const fetchUsers = useCallback(async () => {
    try {
      const response = await api.get(SummaryApi.getAllUsers.url);

      if (response.data.success) {
        // ✅ only active users filter
        const activeUsers = (response.data.data || []).filter(
          (u) => u.isActive !== false
        );

        // convert to dropdown format
        const formatted = activeUsers.map((u) => ({
          label: `${u.firstName} ${u.lastName}`,
          value: u._id
        }));

        setUsers(formatted);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  }, []);

  useEffect(() => {
      fetchDashboardData();
      fetchDivisions();
      fetchUsers(); // 👈 add this
  }, [fetchDashboardData, fetchDivisions, fetchUsers]);


  if (loading) {
    return <FollowUpDashboardSkeleton variant="shimmer" />;
  }

  return (
    <div>


      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col space-y-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
              {viewType === 'user' ? 'My Follow-ups' : 'Follow-up Management'}
            </h2>
            <p className="text-sm sm:text-base text-gray-600 mt-1">Track and manage client follow-ups</p>
          </div>

          {/* Search and Filter */}
          {/* <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search clients..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>

            <select
              value={selectedDivision}
              onChange={(e) => setSelectedDivision(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              <option value="all">All Divisions</option>
              {divisions.map(division => (
                <option key={division._id} value={division._id}>{division.name}</option>
              ))}
            </select>
          </div> */}
          <div className="flex flex-col sm:flex-row gap-3">
          
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search clients..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
          
            {/* 👇 USER DROPDOWN */}
            <SelectDropdown
              options={[
                { label: "All Users", value: "" },
                ...users
              ]}
              value={selectedUser}
              onChange={(val) => setSelectedUser(val)}
              placeholder="Select User"
              searchable
              valueKey="value"
              labelKey="label"
              parentClassName='max-w-[200px]'
            />
          
            {/* 👇 DIVISION DROPDOWN */}
            <SelectDropdown
              options={[
                { label: "All Divisions", value: "all" },
                ...divisions.map((d) => ({
                  label: d.name,
                  value: d._id
                }))
              ]}
              value={selectedDivision}
              onChange={(val) => setSelectedDivision(val)}
              placeholder="Select Division"
              valueKey="value"
              labelKey="label"
              parentClassName='max-w-[200px]'
            />
          
          </div>
        </div>

        {/* Summary Cards */}
        {dashboardData && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {/* Today's Follow-ups */}
            <div
              className={`bg-white rounded-xl shadow-sm border-2 p-4 sm:p-6 cursor-pointer transition-all hover:shadow-md ${activeTab === 'today' ? 'border-red-600' : 'border-gray-200'
                }`}
              onClick={() => setActiveTab('today')}
            >
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <div className="p-2 sm:p-3 bg-red-50 rounded-lg">
                  <Calendar className="h-5 w-5 sm:h-6 sm:w-6 text-red-600" />
                </div>
                <ChevronRight className={`h-4 w-4 sm:h-5 sm:w-5 transition-transform ${activeTab === 'today' ? 'text-red-600 transform rotate-90' : 'text-gray-400'
                  }`} />
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">Today's Follow-ups</h3>
              <p className="text-2xl sm:text-3xl font-bold text-red-600 mt-1 sm:mt-2">{dashboardData.today || 0}</p>
              <p className="text-xs sm:text-sm text-gray-500 mt-1">Scheduled for today</p>
            </div>

            {/* Upcoming Follow-ups */}
            <div
              className={`bg-white rounded-xl shadow-sm border-2 p-4 sm:p-6 cursor-pointer transition-all hover:shadow-md ${activeTab === 'upcoming' ? 'border-blue-600' : 'border-gray-200'
                }`}
              onClick={() => setActiveTab('upcoming')}
            >
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <div className="p-2 sm:p-3 bg-blue-50 rounded-lg">
                  <Clock className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
                </div>
                <ChevronRight className={`h-4 w-4 sm:h-5 sm:w-5 transition-transform ${activeTab === 'upcoming' ? 'text-blue-600 transform rotate-90' : 'text-gray-400'
                  }`} />
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">Upcoming</h3>
              <p className="text-2xl sm:text-3xl font-bold text-blue-600 mt-1 sm:mt-2">{dashboardData.upcoming?.count || 0}</p>
              <p className="text-xs sm:text-sm text-gray-500 mt-1">Next 7 days</p>
            </div>

            {/* Overdue Follow-ups */}
            <div
              className={`bg-white rounded-xl shadow-sm border-2 p-4 sm:p-6 cursor-pointer transition-all hover:shadow-md ${activeTab === 'overdue' ? 'border-orange-600' : 'border-gray-200'
                }`}
              onClick={() => setActiveTab('overdue')}
            >
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <div className="p-2 sm:p-3 bg-orange-50 rounded-lg">
                  <AlertCircle className="h-5 w-5 sm:h-6 sm:w-6 text-orange-600" />
                </div>
                <ChevronRight className={`h-4 w-4 sm:h-5 sm:w-5 transition-transform ${activeTab === 'overdue' ? 'text-orange-600 transform rotate-90' : 'text-gray-400'
                  }`} />
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">Overdue</h3>
              <p className="text-2xl sm:text-3xl font-bold text-orange-600 mt-1 sm:mt-2">{dashboardData.overdue?.count || 0}</p>
              <p className="text-xs sm:text-sm text-gray-500 mt-1">Need attention</p>
            </div>
          </div>
        )}

        {/* Follow-up List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-4 sm:p-6 border-b border-gray-200">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">
              {activeTab === 'today' && "Today's Follow-ups"}
              {activeTab === 'upcoming' && "Upcoming Follow-ups"}
              {activeTab === 'overdue' && "Overdue Follow-ups"}
            </h3>
          </div>

          <div className="divide-y divide-gray-200">
            {/* Today's Follow-ups - Using dedicated endpoint */}
            {activeTab === 'today' && (
              filterFollowUps(todayFollowUps).map((followUp) => (
                <FollowUpCard
                  key={followUp._id}
                  followUp={followUp}
                  onFollowUpClick={handleFollowUpClick}
                  onViewDetails={handleViewDetails}
                  formatDate={formatDate}
                  getDaysUntil={getDaysUntil}
                />
              ))
            )}

            {/* Upcoming Follow-ups */}
            {activeTab === 'upcoming' && (
              filterFollowUps(upcomingFollowUps).map((followUp) => (
                <FollowUpCard
                  key={followUp._id}
                  followUp={followUp}
                  onFollowUpClick={handleFollowUpClick}
                  onViewDetails={handleViewDetails}
                  formatDate={formatDate}
                  getDaysUntil={getDaysUntil}
                />
              ))
            )}

            {/* Overdue Follow-ups */}
            {activeTab === 'overdue' && (
              filterFollowUps(overdueFollowUps).map((followUp) => (
                <FollowUpCard
                  key={followUp._id}
                  followUp={followUp}
                  onFollowUpClick={handleFollowUpClick}
                  onViewDetails={handleViewDetails}
                  formatDate={formatDate}
                  getDaysUntil={getDaysUntil}
                  isOverdue={true}
                />
              ))
            )}

            {/* Empty State */}
            {((activeTab === 'today' && filterFollowUps(todayFollowUps).length === 0) ||
              (activeTab === 'upcoming' && filterFollowUps(upcomingFollowUps).length === 0) ||
              (activeTab === 'overdue' && filterFollowUps(overdueFollowUps).length === 0)) && (
                <div className="p-8 sm:p-12 text-center">
                  <div className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mb-3 sm:mb-4">
                    {activeTab === 'today' && <Calendar className="h-full w-full" />}
                    {activeTab === 'upcoming' && <Clock className="h-full w-full" />}
                    {activeTab === 'overdue' && <AlertCircle className="h-full w-full" />}
                  </div>
                  <p className="text-sm sm:text-base text-gray-500">
                    {activeTab === 'today' && "No follow-ups scheduled for today"}
                    {activeTab === 'upcoming' && "No upcoming follow-ups"}
                    {activeTab === 'overdue' && "No overdue follow-ups"}
                  </p>
                </div>
              )}
          </div>
        </div>



      </div>
      {/* Follow-up Form */}
      {selectedVisit && (
        <FollowUpForm
          visitId={selectedVisit._id}
          clientId={selectedVisit.clientId}
          isOpen={showFollowUpForm}
          onClose={() => setShowFollowUpForm(false)}
          onSuccess={handleFollowUpSuccess}
        />
      )}
    </div>

  );
};

// Follow-up Card Component
const FollowUpCard = ({ followUp, onFollowUpClick, onViewDetails, formatDate, getDaysUntil, isOverdue = false }) => {
  const daysUntil = getDaysUntil(followUp.nextFollowUpDate);

  return (
    <div className="p-4 sm:p-6 hover:bg-gray-50 transition-colors">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-start gap-3 sm:gap-4">
            <div className={`p-1.5 sm:p-2 rounded-lg flex-shrink-0 ${isOverdue ? 'bg-orange-50' :
                daysUntil === 0 ? 'bg-red-50' :
                  'bg-blue-50'
              }`}>
              <Phone className={`h-4 w-4 sm:h-5 sm:w-5 ${isOverdue ? 'text-orange-600' :
                  daysUntil === 0 ? 'text-red-600' :
                    'text-blue-600'
                }`} />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <h4 className="font-medium text-sm sm:text-base text-gray-900 truncate">
                  {followUp.clientName}
                </h4>
                <span className="text-xs sm:text-sm text-gray-500">({followUp.clientId})</span>
                {followUp.stageId && (
                  <span
                    className="px-2 py-0.5 text-xs font-medium rounded-full"
                    style={{
                      backgroundColor: `${followUp.stageId.color}20`,
                      color: followUp.stageId.color
                    }}
                  >
                    {followUp.stageId.stageName}
                  </span>
                )}
              </div>

              <p className="text-xs sm:text-sm text-gray-600 mb-2 truncate">
                Contact: {followUp.contactName} • {followUp.contactPhone}
              </p>

              <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm">
                <span className={`font-medium ${isOverdue ? 'text-orange-600' :
                    daysUntil === 0 ? 'text-red-600' :
                      'text-blue-600'
                  }`}>
                  {isOverdue ? `${Math.abs(daysUntil)} days overdue` :
                    daysUntil === 0 ? 'Due today' :
                      `Due in ${daysUntil} days`}
                </span>
                <span className="text-gray-500">

                  {formatDate(followUp.nextFollowUpDate)}

                  {followUp.nextFollowUpTime && (
                    <>
                      {" "}at{" "}
                      {new Date(
                        `1970-01-01T${followUp.nextFollowUpTime}`
                      ).toLocaleTimeString("en-IN", {
                        hour: "numeric",
                        minute: "2-digit",
                        hour12: true
                      })}
                    </>
                  )}

                </span>
                {followUp.divisionId && (
                  <span className="text-gray-500 hidden sm:inline">
                    {followUp.divisionId.name}
                  </span>
                )}
                {followUp.userId && (
                  <span className="text-gray-500 hidden lg:inline">
                    {followUp.userId.firstName} {followUp.userId.lastName}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:flex-shrink-0">
          <button
            onClick={() => onViewDetails(followUp)}
            className="flex-1 sm:flex-initial px-3 py-1.5 sm:px-4 sm:py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-xs sm:text-sm font-medium flex items-center justify-center gap-1"
          >
            <Eye className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="hidden sm:inline">View</span>
          </button>
          <button
            onClick={() => onFollowUpClick(followUp)}
            className="flex-1 sm:flex-initial px-3 py-1.5 sm:px-4 sm:py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-xs sm:text-sm font-medium"
          >
            Add Follow-up
          </button>
        </div>
      </div>
    </div>
  );
};

export default FollowUpDashboard;
